/**
 * Characters that draw nothing yet change what a model or a reviewer reads:
 * every Unicode format character (\p{Cf}: bidi embeddings, overrides and
 * isolates, zero-width marks, soft hyphen U+00AD, Mongolian vowel separator
 * U+180E, BOM, interlinear annotation U+FFF9–FFFB, and the tag block
 * U+E0000–E007F that smuggles ASCII past a reader), plus blank characters
 * that are letters rather than format characters: the Hangul fillers U+115F,
 * U+1160, U+3164 and U+FFA0, and the supplementary
 * variation selectors U+E0100–E01EF that hide bytes after a visible character.
 * U+180E is listed explicitly because older Unicode versions class it as a
 * space. The BMP variation selectors stay: emoji need them.
 *
 * One list for every sanitizer in the repo. Copies that cannot import it
 * (electron/, the vault-agent connector bundle, the subagent loop core) mirror it, and
 * tests/shared/hiddenCharacters.test.ts holds each copy to the same set.
 *
 * A character-class body for a regular expression with the `u` flag.
 */
export const HIDDEN_FORMAT_CHARACTERS = String.raw`\p{Cf}\u115F\u1160\u180E\u3164\uFFA0\u{E0000}-\u{E007F}\u{E0100}-\u{E01EF}`;

/** Zero-width non-joiner, joiner, and the left-to-right and right-to-left marks. */
const JOINERS = String.raw`\u200C-\u200F`;

/**
 * Format characters that draw a sign: the prepended concatenation marks
 * (Arabic number signs U+0600–0605, end of ayah U+06DD, Syriac abbreviation
 * mark U+070F, Arabic pound and piastre marks U+0890–0891, disputed end of
 * ayah U+08E2, Kaithi number signs U+110BD and U+110CD). They span the digits
 * after them and cannot hide or reorder text. A character-class body.
 */
export const VISIBLE_FORMAT_CHARACTERS = String.raw`\u0600-\u0605\u06DD\u070F\u0890\u0891\u08E2\u{110BD}\u{110CD}`;

/**
 * The tag spellings of the only recommended (RGI) emoji tag sequences: the
 * flags of England, Scotland and Wales, each the black flag U+1F3F4, the
 * subdivision code as tag letters, and the cancel tag U+E007F. A fixed list,
 * so a kept sequence can carry no smuggled text.
 */
const FLAG_SUBDIVISIONS = ["gbeng", "gbsct", "gbwls"] as const;
const tagOf = (code: number) => `\\u{${code.toString(16).toUpperCase()}}`;

/**
 * A lookaround that holds at a tag character inside one of those flags: the
 * black flag and the earlier tags behind it, the rest of the sequence ahead.
 */
const FLAG_TAG_POSITION = FLAG_SUBDIVISIONS.flatMap((subdivision) => {
  const tags = [...Array.from(subdivision, (letter) => tagOf(0xE0000 + letter.charCodeAt(0))), tagOf(0xE007F)];
  return tags.map((_, at) => `(?<=\\u{1F3F4}${tags.slice(0, at).join("")})${tags.slice(at).join("")}`);
}).join("|");

export type HiddenCharacterOptions = Readonly<{
  /**
   * Keep ZWNJ, ZWJ, LRM and RLM: emoji sequences and several scripts need
   * them, and they cannot reorder text the way overrides can.
   */
  keepJoiners?: boolean;
  /**
   * Keep format characters a reader sees: VISIBLE_FORMAT_CHARACTERS, and the
   * tags of a whole England, Scotland or Wales flag (a stray or partial tag
   * still goes). For text a person reads; a sanitizer guarding a model or a
   * link check leaves it off and strips every format character.
   */
  keepVisibleFormat?: boolean;
}>;

/**
 * A global pattern for one hidden character, with `extra` class ranges the
 * caller also strips (its own control characters, say). Without options it
 * strips the whole list, as security sanitizers need; display callers opt
 * into keeping joiners and visible format characters.
 */
export function hiddenCharacterPattern(extra = "", options: HiddenCharacterOptions = {}): RegExp {
  const kept = [
    ...(options.keepJoiners ? [`[${JOINERS}]`] : []),
    ...(options.keepVisibleFormat ? [`[${VISIBLE_FORMAT_CHARACTERS}]`, FLAG_TAG_POSITION] : []),
  ];
  const guard = kept.length > 0 ? `(?!${kept.join("|")})` : "";
  return new RegExp(`${guard}[${HIDDEN_FORMAT_CHARACTERS}${extra}]`, "gu");
}
